import { useState } from 'react'
import { Loader2, Stethoscope } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/contexts/AuthContext'

export default function LoginPage() {
  const { allowSelfSignup } = useAuth()
  const [mode, setMode] = useState<'login' | 'signup' | 'reset'>(allowSelfSignup ? 'login' : 'login')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setAviso(null)
    setBusy(true)
    const supabase = createClient()
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password: senha })
        if (error) {
          setErro(error.code === 'email_not_confirmed'
            ? 'Confirme seu e-mail antes de entrar (verifique sua caixa de entrada).'
            : 'E-mail ou senha inválidos.')
          return
        }
        window.location.href = '/'
      } else if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({ email, password: senha })
        if (error) { setErro(error.message); return }
        setAviso('Conta criada. Se a confirmação por e-mail estiver ativa, confirme antes de entrar.')
        setMode('login')
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email)
        if (error) { setErro(error.message); return }
        setAviso('Se o e-mail existir, você receberá o link de recuperação.')
        setMode('login')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="public-screen">
      <div className="card" style={{ width: '100%', maxWidth: 400, padding: '32px 28px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div
            aria-hidden
            style={{
              width: 52, height: 52, borderRadius: 14, background: 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
              marginBottom: 12,
            }}
          >
            <Stethoscope size={26} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>Médico 360</h1>
          <p className="page-subtitle" style={{ margin: '4px 0 0' }}>Sua rotina clínica sob controle.</p>
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label className="field-label" htmlFor="email">E-mail</label>
            <input
              id="email" type="email" required autoComplete="email"
              className="field-input" value={email} onChange={e => setEmail(e.target.value)}
            />
          </div>

          {mode !== 'reset' && (
            <div>
              <label className="field-label" htmlFor="senha">Senha</label>
              <input
                id="senha" type="password" required minLength={8}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                className="field-input" value={senha} onChange={e => setSenha(e.target.value)}
              />
            </div>
          )}

          {erro && <div className="field-error" role="alert">{erro}</div>}
          {aviso && <div className="badge badge-info" role="status" style={{ alignSelf: 'flex-start' }}>{aviso}</div>}

          <button type="submit" className="btn-primary" disabled={busy}>
            {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
            {mode === 'login' ? 'Entrar' : mode === 'signup' ? 'Criar conta' : 'Recuperar senha'}
          </button>
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </form>

        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
          {mode === 'login' ? (
            <>
              {allowSelfSignup ? (
                <button type="button" onClick={() => { setMode('signup'); setErro(null) }} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600 }}>
                  Criar conta
                </button>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Acesso provisionado pela operação Soumed</span>
              )}
              <button type="button" onClick={() => { setMode('reset'); setErro(null) }} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)' }}>
                Esqueci a senha
              </button>
            </>
          ) : (
            <button type="button" onClick={() => { setMode('login'); setErro(null) }} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600 }}>
              Voltar ao login
            </button>
          )}
        </div>
        <div style={{ marginTop: 6, textAlign: 'center', display: mode === 'login' ? 'none' : 'block' }}>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {mode === 'signup' ? 'Você criará seu workspace no próximo passo.' : ''}
          </span>
        </div>
      </div>
    </div>
  )
}
